import { Lightning as lng, Utils } from '@lightningjs/sdk';
import { CreditTypes } from '../../Business/detail/detail.types';
import { DetailTranslationKeys } from '../../api/models/TranslationKeys';
import { ProgressBar } from '../../components/shared/ProgressBar';
import { TextIconsRow } from '../../components/shared/TextIconsRow';
import { Theme } from '../../config/theme';

const Constants = {
  Indent: 20,
  Description: {
    width: 980,
    miniWidth: 1100,
    progressBar: {
      width: 618,
      height: 8,
    },
  },
  Icons: {
    size: 40,
    ratingIconsSize: 28,
    ratingIconSpacing: 8,
    marginLeft: 16,
  },
  Synopsis: {
    maxLines: 7,
    miniMaxLines: 3,
    autoplayMaxLines: 3,
  },
  Margins: {
    xlarge: 30,
    large: 24,
    medium: 14,
    small: 4,
  },
  OperatorScore: {
    totalStars: 4,
    IconDisabled: 0xff757575,
  },
  Credits: {
    namesMarginLeft: 200,
    namesWrapWidth: 700,
  },
};

export class DetailDescription extends lng.Component {
  static _template() {
    return {
      flex: { direction: 'column', paddingLeft: Constants.Indent },
      Title: {
        h: 70,
        TitleBackground: {
          x: -Constants.Indent,
          src: Utils.asset('/images/detail_title_background.png'),
          color: Theme.Colors.Primary,
        },
        TitleText: {
          y: h => h / 2,
          mountY: 0.5,
          text: {
            ...Theme.Fonts.Detail.Title,
            verticalAlign: 'middle',
            wordWrapWidth: Constants.Description.width,
            wordWrap: false,
            textOverflow: 'ellipsis',
          },
        },
      },
      Episode: {
        visible: false,
        flexItem: { marginTop: Constants.Margins.medium },
        type: TextIconsRow,
        w: Constants.Description.width,
      },
      RecordingPricingInfo: {
        visible: false,
        flexItem: {},
        flex: { direction: 'column' },
        ExpireInfo: {
          text: {
            ...Theme.Fonts.Detail.ExpireInfo,
          },
        },
        RecordingError: {
          text: { ...Theme.Fonts.Bold },
        },
        Pricing: {
          text: { ...Theme.Fonts.Bold, advancedRenderer: true },
        },
        Subscription: {
          text: { ...Theme.Fonts.Bold },
        },
      },
      Synopsis: {
        flexItem: { marginTop: Constants.Margins.medium },
        text: {
          ...Theme.Fonts.Normal,
          wordWrap: true,
          wordWrapWidth: Constants.Description.width,
          maxLines: Constants.Synopsis.maxLines,
          advancedRenderer: true,
          text: '',
        },
      },
      SecondaryMeta: {
        visible: true,
        flexItem: { marginTop: Constants.Margins.large },
        type: TextIconsRow,
        w: Constants.Description.width,
      },
      Credits: {
        visible: false,
        flexItem: { marginTop: Constants.Margins.large },
        flex: { direction: 'column' },
      },
      Scores: {
        visible: false,
        flexItem: { marginTop: Constants.Margins.large },
        flex: { direction: 'row' },
        ImdbRating: {
          text: {
            ...Theme.Fonts.Normal,
            lineHeight: null,
            verticalAlign: 'center',
            text: '',
          },
        },
        Separator: {
          text: {
            ...Theme.Fonts.Normal,
            // verticalAlign: 'center',
            lineHeight: null,
            text: '  •  ',
          },
        },
        OperatorScore: {
          h: Constants.Icons.ratingIconsSize + 10,
          flexItem: { alignSelf: 'center' },
        },
      },
      Blackout: {
        visible: false,
        flexItem: { marginTop: Constants.Margins.large },
        text: {
          ...Theme.Fonts.Bold,
          text: '',
        },
      },
      ProgressBar: {
        visible: false,
        flexItem: { marginTop: Constants.Margins.xlarge },
        type: ProgressBar,
        w: Constants.Description.progressBar.width,
        h: Constants.Description.progressBar.height,
      },
    };
  }

  _init() {
    if (this.miniDetail) {
      this.tag('Synopsis').text.patch({
        wordWrapWidth: Constants.Description.miniWidth,
        maxLines: Constants.Synopsis.miniMaxLines,
      });
      this.tag('TitleBackground').patch({
        w: 1180,
      });
    } else if (this.autoplayScreen) {
      this.tag('Synopsis').text.patch({
        maxLines: Constants.Synopsis.autoplayMaxLines,
      });
    }
  }

  /**
   * @param {IDetailDescription} val
   */
  set content(val) {
    // console.log(val);
    this._content = val;
    this.update();
  }

  update() {
    const content = this._content;
    // console.log(content);
    this.tag('TitleText').text.text = content.title || '';
    this.updateTextValue('Synopsis', content.synopsis);

    this.tag('Episode').textData = {
      ...Theme.Fonts.Detail.MetaInfo,
      text: content.metaInformation || '',
    };

    if (!content.metaInformation) this.tag('RecordingPricingInfo').flexItem.marginTop = Constants.Margins.medium;
    // console.log(Constants.Margins.medium);

    /**
     * @type {IconDef[]}
     */
    let playIcons = [];
    if (this.miniDetail) {
      if (content.accessibilityIcons) playIcons = playIcons.concat(content.accessibilityIcons);
    }
    if (content.recordingIcons) playIcons = playIcons.concat(content.recordingIcons);
    if (content.play) playIcons.push(content.play);
    this.tag('Episode').icons = playIcons;
    this.tag('Episode').visible = !!content.metaInformation || playIcons.length > 0;

    this.updateTextValue('ExpireInfo', content.expire, true);
    this.updateTextValue('RecordingError', content.recordingError, true);
    this.updateTextValue('Pricing', content.pricing, true);
    this.updateTextValue('Subscription', content.subscription, true);
    this.updateTextValue('Blackout', content.blackout, false);

    if (content.secondaryMetaInfo) {
      this.tag('SecondaryMeta').textData = {
        ...Theme.Fonts.Normal,
        text: content.secondaryMetaInfo,
        fontFace: 'Regular',
      };
    }

    let secondaryMetaIcons = [];
    if (!!content.ageRating) secondaryMetaIcons.push(content.ageRating);
    if (!this.miniDetail) {
      if (content.accessibilityIcons) secondaryMetaIcons = secondaryMetaIcons.concat(content.accessibilityIcons);
    }
    if (content.parentalControlIcons && content.parentalControlIcons.length > 0)
      secondaryMetaIcons = secondaryMetaIcons.concat(content.parentalControlIcons);
    if (!!content.resolution) secondaryMetaIcons.push(content.resolution);
    this.tag('SecondaryMeta').iconSize = Constants.Icons.size;
    this.tag('SecondaryMeta').icons = secondaryMetaIcons;

    this.tag('SecondaryMeta').visible = !!content.secondaryMetaInfo || secondaryMetaIcons.length > 0;

    this.setCredits();

    this.setScores();

    this.tag('ProgressBar').visible = !!content.bookmarkPercentage;
    this.tag('ProgressBar').fillPercentage = content.bookmarkPercentage;
  }

  /**
   * set the value of the text, hide element if empty value
   * @param lngKey
   * @param value
   * @param unhideParent
   */
  updateTextValue(lngKey, value, unhideParent = false) {
    this.tag(lngKey).visible = !!value;
    this.tag(lngKey).text.text = value;
    if (unhideParent && !!value) this.tag(lngKey).parent.visible = true;
  }

  setCredits() {
    const credits = this._content.credits;
    if (!credits) return;
    this.tag('Credits').childList.clear();
    this.tag('Credits').visible = false;
    this.addCredit(CreditTypes.DIRECTOR, credits.get(CreditTypes.DIRECTOR));
    this.addCredit(CreditTypes.ACTOR, credits.get(CreditTypes.ACTOR));
  }

  addCredit(key, value) {
    if (!value) return;
    this.tag('Credits').visible = true;
    this.tag('Credits').childList.a({
      flex: { direction: 'row' },
      Role: {
        flexItem: { minWidth: 250 },
        h: Theme.Fonts.Normal.lineHeight,
        Text: {
          text: {
            ...Theme.Fonts.Normal,
            text: this.getTranslationForCredit(key),
          },
        },
      },
      Names: {
        text: {
          ...Theme.Fonts.Normal,
          maxLines: 2,
          wordWrapWidth: Constants.Credits.namesWrapWidth,
          text: value.names.join(', '),
        },
      },
    });
  }

  getTranslationForCredit(credit) {
    const backend = this.params.backend;
    switch (credit) {
      case CreditTypes.ACTOR:
        return backend.translations.forKey(DetailTranslationKeys.detail_actors);
      case CreditTypes.DIRECTOR:
        return backend.translations.forKey(DetailTranslationKeys.detail_directors);
      default:
        return undefined;
    }
  }

  setScores() {
    const scores = this._content.score;
    if (!scores) return;
    this.tag('Scores').visible = !!scores.imdb || !!scores.operator;
    this.tag('Scores').tag('Separator').visible = !!scores.imdb && !!scores.operator;

    if (scores.imdb) {
      let imdbScore = (scores.imdb / 10).toFixed(1);
      if (imdbScore === '10.0') imdbScore = '10';
      let imdbTranslation = this.params.backend.translations.forKey(DetailTranslationKeys.detail_imdb_rating);
      this.tag('ImdbRating').text.text = `${imdbTranslation} ${imdbScore}/10`;
    }

    if (scores.operator) {
      let nrStars = Math.ceil(scores.operator / 25);
      for (let i = 0; i < nrStars; i++) {
        this.tag('OperatorScore').childList.a({
          x: i * (Constants.Icons.ratingIconsSize + Constants.Icons.ratingIconSpacing),
          w: Constants.Icons.ratingIconsSize,
          h: Constants.Icons.ratingIconsSize,
          src: Utils.asset('/images/detail/ic_star.png'),
        });
      }
      for (let i = nrStars; i < Constants.OperatorScore.totalStars; i++) {
        this.tag('OperatorScore').childList.a({
          x: i * (Constants.Icons.ratingIconsSize + Constants.Icons.ratingIconSpacing),
          w: Constants.Icons.ratingIconsSize,
          h: Constants.Icons.ratingIconsSize,
          color: Constants.OperatorScore.IconDisabled,
          src: Utils.asset('/images/detail/ic_star.png'),
        });
      }
    }
  }
}
